import { __rest } from "tslib";
import * as React from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { Check, ChevronRight, Circle } from "lucide-react";
import { cn } from "../../lib/utils";
/**
 * @example
 *
 * ```jsx
 * import {
 *   Cloud,
 *   CreditCard,
 *   Github,
 *   Keyboard,
 *   LifeBuoy,
 *   LogOut,
 *   Mail,
 *   MessageSquare,
 *   Plus,
 *   PlusCircle,
 *   Settings,
 *   User,
 *   UserPlus,
 *   Users,
 * } from "lucide-react"
 *
 * import {
 *   Button,
 *   DropdownMenu,
 *   DropdownMenuContent,
 *   DropdownMenuGroup,
 *   DropdownMenuItem,
 *   DropdownMenuLabel,
 *   DropdownMenuPortal,
 *   DropdownMenuSeparator,
 *   DropdownMenuShortcut,
 *   DropdownMenuSub,
 *   DropdownMenuSubContent,
 *   DropdownMenuSubTrigger,
 *   DropdownMenuTrigger,
 * } from "@skve/components"
 *
 * function Demo() {
 *   return (
 *     <DropdownMenu>
 *       <DropdownMenuTrigger asChild>
 *         <Button variant="outline">Open</Button>
 *       </DropdownMenuTrigger>
 *       <DropdownMenuContent className="w-56">
 *         <DropdownMenuLabel>My Account</DropdownMenuLabel>
 *         <DropdownMenuSeparator />
 *         <DropdownMenuGroup>
 *           <DropdownMenuItem>
 *             <User />
 *             <span>Profile</span>
 *             <DropdownMenuShortcut>⇧⌘P</DropdownMenuShortcut>
 *           </DropdownMenuItem>
 *           <DropdownMenuItem>
 *             <CreditCard />
 *             <span>Billing</span>
 *             <DropdownMenuShortcut>⌘B</DropdownMenuShortcut>
 *           </DropdownMenuItem>
 *           <DropdownMenuItem>
 *             <Settings />
 *             <span>Settings</span>
 *             <DropdownMenuShortcut>⌘S</DropdownMenuShortcut>
 *           </DropdownMenuItem>
 *           <DropdownMenuItem>
 *             <Keyboard />
 *             <span>Keyboard shortcuts</span>
 *             <DropdownMenuShortcut>⌘K</DropdownMenuShortcut>
 *           </DropdownMenuItem>
 *         </DropdownMenuGroup>
 *         <DropdownMenuSeparator />
 *         <DropdownMenuGroup>
 *           <DropdownMenuItem>
 *             <Users />
 *             <span>Team</span>
 *           </DropdownMenuItem>
 *           <DropdownMenuSub>
 *             <DropdownMenuSubTrigger>
 *               <UserPlus />
 *               <span>Invite users</span>
 *             </DropdownMenuSubTrigger>
 *             <DropdownMenuPortal>
 *               <DropdownMenuSubContent>
 *                 <DropdownMenuItem>
 *                   <Mail />
 *                   <span>Email</span>
 *                 </DropdownMenuItem>
 *                 <DropdownMenuItem>
 *                   <MessageSquare />
 *                   <span>Message</span>
 *                 </DropdownMenuItem>
 *                 <DropdownMenuSeparator />
 *                 <DropdownMenuItem>
 *                   <PlusCircle />
 *                   <span>More...</span>
 *                 </DropdownMenuItem>
 *               </DropdownMenuSubContent>
 *             </DropdownMenuPortal>
 *           </DropdownMenuSub>
 *           <DropdownMenuItem>
 *             <Plus />
 *             <span>New Team</span>
 *             <DropdownMenuShortcut>⌘+T</DropdownMenuShortcut>
 *           </DropdownMenuItem>
 *         </DropdownMenuGroup>
 *         <DropdownMenuSeparator />
 *         <DropdownMenuItem>
 *           <Github />
 *           <span>GitHub</span>
 *         </DropdownMenuItem>
 *         <DropdownMenuItem>
 *           <LifeBuoy />
 *           <span>Support</span>
 *         </DropdownMenuItem>
 *         <DropdownMenuItem disabled>
 *           <Cloud />
 *           <span>API</span>
 *         </DropdownMenuItem>
 *         <DropdownMenuSeparator />
 *         <DropdownMenuItem>
 *           <LogOut />
 *           <span>Log out</span>
 *           <DropdownMenuShortcut>⇧⌘Q</DropdownMenuShortcut>
 *         </DropdownMenuItem>
 *       </DropdownMenuContent>
 *     </DropdownMenu>
 *   )
 * }
 * ```
 */
const DropdownMenu = DropdownMenuPrimitive.Root;
const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
const DropdownMenuGroup = DropdownMenuPrimitive.Group;
const DropdownMenuPortal = DropdownMenuPrimitive.Portal;
const DropdownMenuSub = DropdownMenuPrimitive.Sub;
const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;
const DropdownMenuSubTrigger = React.forwardRef((_a, ref) => {
    var { className, inset, children } = _a, props = __rest(_a, ["className", "inset", "children"]);
    return (React.createElement(DropdownMenuPrimitive.SubTrigger, Object.assign({ ref: ref, className: cn("flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none focus:bg-accent data-[state=open]:bg-accent", inset && "pl-8", className) }, props),
        children,
        React.createElement(ChevronRight, { className: "ml-auto h-4 w-4" })));
});
DropdownMenuSubTrigger.displayName =
    DropdownMenuPrimitive.SubTrigger.displayName;
const DropdownMenuSubContent = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React.createElement(DropdownMenuPrimitive.SubContent, Object.assign({ ref: ref, className: cn("z-[500] min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2", className) }, props)));
});
DropdownMenuSubContent.displayName =
    DropdownMenuPrimitive.SubContent.displayName;
const DropdownMenuContent = React.forwardRef((_a, ref) => {
    var { className, sideOffset = 4 } = _a, props = __rest(_a, ["className", "sideOffset"]);
    return (React.createElement(DropdownMenuPrimitive.Portal, null,
        React.createElement(DropdownMenuPrimitive.Content, Object.assign({ ref: ref, sideOffset: sideOffset, className: cn("z-[500] min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2", className) }, props))));
});
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;
const DropdownMenuItem = React.forwardRef((_a, ref) => {
    var { className, inset } = _a, props = __rest(_a, ["className", "inset"]);
    return (React.createElement(DropdownMenuPrimitive.Item, Object.assign({ ref: ref, className: cn("relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50", inset && "pl-8", className) }, props)));
});
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;
const DropdownMenuCheckboxItem = React.forwardRef((_a, ref) => {
    var { className, children, checked } = _a, props = __rest(_a, ["className", "children", "checked"]);
    return (React.createElement(DropdownMenuPrimitive.CheckboxItem, Object.assign({ ref: ref, className: cn("relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50", className), checked: checked }, props),
        React.createElement("span", { className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center" },
            React.createElement(DropdownMenuPrimitive.ItemIndicator, null,
                React.createElement(Check, { className: "h-4 w-4" }))),
        children));
});
DropdownMenuCheckboxItem.displayName =
    DropdownMenuPrimitive.CheckboxItem.displayName;
const DropdownMenuRadioItem = React.forwardRef((_a, ref) => {
    var { className, children } = _a, props = __rest(_a, ["className", "children"]);
    return (React.createElement(DropdownMenuPrimitive.RadioItem, Object.assign({ ref: ref, className: cn("relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50", className) }, props),
        React.createElement("span", { className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center" },
            React.createElement(DropdownMenuPrimitive.ItemIndicator, null,
                React.createElement(Circle, { className: "h-2 w-2 fill-current" }))),
        children));
});
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;
const DropdownMenuLabel = React.forwardRef((_a, ref) => {
    var { className, inset } = _a, props = __rest(_a, ["className", "inset"]);
    return (React.createElement(DropdownMenuPrimitive.Label, Object.assign({ ref: ref, className: cn("px-2 py-1.5 text-sm font-semibold", inset && "pl-8", className) }, props)));
});
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;
const DropdownMenuSeparator = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React.createElement(DropdownMenuPrimitive.Separator, Object.assign({ ref: ref, className: cn("-mx-1 my-1 h-px bg-muted", className) }, props)));
});
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;
const DropdownMenuShortcut = (_a) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React.createElement("span", Object.assign({ className: cn("ml-auto text-xs tracking-widest opacity-60", className) }, props)));
};
DropdownMenuShortcut.displayName = "DropdownMenuShortcut";
export { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuCheckboxItem, DropdownMenuRadioItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuGroup, DropdownMenuPortal, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuRadioGroup, };
//# sourceMappingURL=dropdown-menu.js.map